import { ErrorContainer, ErrorDetail } from "@tastyworks/tastyworks-api"
import { _ } from "svelte-i18n"
import { get } from "svelte/store"
import type { RequireAtLeastOne } from "type-fest"
import { toastAlert } from "./commons-web2/toast-alert"

interface BaseErrorParameters<T extends string> {
  name: T
  message: string
  recoverable?: boolean
}

export class BaseError<T extends string> extends Error {
  recoverable: boolean

  constructor({ name, message, recoverable }: BaseErrorParameters<T>) {
    super(message)
    this.name = name
    this.recoverable = recoverable ?? false
  }
}

type UnexpectedStateErrorParameters = Omit<
  BaseErrorParameters<"UnexpectedStateError">,
  "name"
>

/**
 * Represents a custom error class for unexpected states that may occur in the application.
 * This error should be thrown when the application encounters a state that it should not have reached.
 * It will be reported to sentry and should be fixed.
 *
 * This will be very similar in utility to `console.assert(false, "message")`.
 * The main difference is that this will break out of the current execution context and report an error to the user.
 */
export class UnexpectedStateError extends BaseError<"UnexpectedStateError"> {
  constructor(params: UnexpectedStateErrorParameters) {
    super({ name: "UnexpectedStateError", ...params })
    Error.captureStackTrace?.(this, UnexpectedStateError)
  }
}

export enum RestErrorType {
  NotFound = "Not Found",
  BadRequest = "Bad Request",
  Unauthorized = "Unauthorized",
  Forbidden = "Forbidden",
}

export type RestOperation = "create" | "read" | "update" | "delete" | "validate"

// omit name while error type is not in use
type RestErrorParameters<T extends string> = Omit<
  BaseErrorParameters<T>,
  "name" | "message"
> &
  RequireAtLeastOne<
    {
      message?: string // use as i18nPath so that it's not outright ignored
      operation: RestOperation
      resource: string
      errorContainer?: ErrorContainer
    },
    "message" | "errorContainer"
  >

type ModelValidationError = RestError & {
  errorContainer: ErrorContainer
} & { code: "validation_error" }

/**
 * Represents a custom error class for REST API errors.
 */
export class RestError extends BaseError<"RestError"> {
  operation: RestOperation
  resource: string
  errorContainer?: ErrorContainer

  /**
   * Creates a new instance of the RestError class.
   * @param operation The REST operation that caused the error.
   * @param resource The resource associated with the error.
   * @param errorContainer The error container object containing the error details.
   */
  constructor({
    operation,
    resource,
    errorContainer,
    recoverable,
    message,
  }: RestErrorParameters<RestErrorType>) {
    super({
      name: "RestError",
      message: message
        ? get(_)(message)
        : errorContainer
          ? errorContainer.message
          : "",
      recoverable,
    })
    this.operation = operation
    this.resource = resource
    this.errorContainer = errorContainer
    Error.captureStackTrace?.(this, RestError)
  }

  get validationErrors(): ErrorDetail[] {
    if (!this.isValidationError()) {
      return []
    }
    return this.errorContainer.errors
  }

  isValidationError(): this is ModelValidationError {
    return this.errorContainer?.code === "validation_error"
  }
}

export function alertRestError(error: RestError) {
  toastAlert(
    "error",
    get(_)("errors.alertTitle"),
    error.errorContainer?.message,
    error.errorContainer?.errors.map(
      (e) => e.message || `${e.domain} ${e.reason}`
    )
  )
}

/* istanbul ignore next -- @preserve */
if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("RestError", async () => {
    const errorContainer = new ErrorContainer()
    errorContainer.code = "validation_error"
    errorContainer.message = "Failed to validate"
    const detail = new ErrorDetail()
    detail.domain = "email"
    detail.reason = "is invalid"
    errorContainer.errors = [detail]

    const error = new RestError({
      message: "Unable to create customer",
      operation: "create",
      resource: "Customer",
      errorContainer,
    })
    it("should be a subclass of Error", () => {
      expect(error).toBeInstanceOf(Error)
    })

    it("should have a stack trace when thrown", () => {
      try {
        throw error
      } catch (error) {
        if (error instanceof RestError) {
          expect(error.stack).toBeDefined()
        } else {
          expect.fail("error is not an instance of RestError")
        }
      }
    })
  })
}

export function createSessionInvalidationError() {
  return new RestError({
    operation: "validate",
    resource: "Customer",
    recoverable: true,
    message: get(_)(
      "personalInfo.affiliationInformation.sessionInvalidationMessage"
    ),
  })
}
